/**
 * Temporal Utilities
 *
 * Helpers for creating year-over-year computation nodes
 * Expands templates like "propertyValue" into "propertyValue.year0", "propertyValue.year1", etc.
 */

import { NodeId, ComputedNode, Node, Unit, SeriesPatternType, SeriesMeta } from './types';

/**
 * Template for generating temporal nodes
 */
export interface TemporalNodeTemplate {
  // Base ID (e.g., "propertyValue" -> "propertyValue.year0", "propertyValue.year1", ...)
  baseId: string;
  // Human-readable label template (use {year} for substitution)
  labelTemplate: string;
  // Description template
  descriptionTemplate?: string;
  // Unit for all generated nodes
  unit: Unit;
  // Formula template (use {year} for substitution)
  formulaTemplate: string;
  // Generator function that returns deps and compute for each year
  generator: (year: number) => {
    deps: NodeId[];
    compute: (...values: number[]) => number;
  };
}

/**
 * Generate a temporal node ID
 */
export function temporalId(baseId: string, year: number): NodeId {
  return `${baseId}.year${year}`;
}

/**
 * Parse a temporal node ID to extract base and year
 */
export function parseTemporalId(nodeId: NodeId): { baseId: string; year: number } | null {
  const match = nodeId.match(/^(.+)\.year(\d+)$/);
  if (!match) return null;
  return { baseId: match[1], year: parseInt(match[2], 10) };
}

/**
 * Expand a temporal template into individual year nodes
 */
export function expandTemporalNodes(
  template: TemporalNodeTemplate,
  years: number = 11 // 0-10 inclusive
): Record<NodeId, ComputedNode> {
  const nodes: Record<NodeId, ComputedNode> = {};

  for (let year = 0; year < years; year++) {
    const id = temporalId(template.baseId, year);
    const { deps, compute } = template.generator(year);

    nodes[id] = {
      kind: 'computed',
      id,
      label: template.labelTemplate.replace('{year}', String(year)),
      description: template.descriptionTemplate?.replace('{year}', String(year)),
      unit: template.unit,
      deps,
      compute,
      formula: template.formulaTemplate.replace('{year}', String(year)),
    };
  }

  return nodes;
}

/**
 * Create series metadata for a node
 */
function createSeriesMeta(
  seriesId: string,
  pattern: SeriesPatternType,
  year: number,
  totalYears: number,
  seriesLabel: string
): SeriesMeta {
  return { seriesId, pattern, year, totalYears, seriesLabel };
}

/**
 * Create a simple growing value (compound growth)
 * Uses direct formula: initial × (1 + rate/100)^year
 * This avoids recursive dependencies, making derivation trees much simpler.
 */
export function compoundGrowth(
  baseId: string,
  config: {
    initialValueId: NodeId;
    growthRateId: NodeId;
    label: string;
    unit: Unit;
    years?: number;
  }
): Record<NodeId, ComputedNode> {
  const { initialValueId, growthRateId, label, unit, years = 11 } = config;

  const nodes: Record<NodeId, ComputedNode> = {};

  for (let year = 0; year < years; year++) {
    const id = temporalId(baseId, year);
    const seriesMeta = createSeriesMeta(baseId, 'compound-growth', year, years, label);

    if (year === 0) {
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year 0)`,
        unit,
        deps: [initialValueId],
        compute: (initial) => initial,
        formula: `{${initialValueId}}`,
        seriesMeta,
      };
    } else {
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit,
        deps: [initialValueId, growthRateId],
        compute: (initial, rate) => initial * Math.pow(1 + rate / 100, year),
        formula: `{${initialValueId}} × (1 + {${growthRateId}} / 100)^${year}`,
        seriesMeta,
      };
    }
  }

  return nodes;
}

/**
 * Create a temporal node that depends on multiple other temporal values at the same year
 * Example: netRentalIncome.year5 = grossRent.year5 - expenses.year5
 */
export function temporalComputed(
  baseId: string,
  config: {
    label: string;
    unit: Unit;
    // Dependencies can be temporal (same year) or static
    deps: Array<{ id: NodeId; temporal: boolean }>;
    compute: (...values: number[]) => number;
    formula: string;
    years?: number;
  }
): Record<NodeId, ComputedNode> {
  const { label, unit, deps, compute, formula, years = 11 } = config;

  const nodes: Record<NodeId, ComputedNode> = {};

  for (let year = 0; year < years; year++) {
    const id = temporalId(baseId, year);
    const seriesMeta = createSeriesMeta(baseId, 'temporal-computed', year, years, label);
    const resolvedDeps = deps.map((d) =>
      d.temporal ? temporalId(d.id, year) : d.id
    );

    // Replace temporal dep references in formula
    let resolvedFormula = formula;
    for (const d of deps) {
      if (d.temporal) {
        resolvedFormula = resolvedFormula.replace(
          new RegExp(`\\{${d.id}\\}`, 'g'),
          `{${temporalId(d.id, year)}}`
        );
      }
    }

    nodes[id] = {
      kind: 'computed',
      id,
      label: `${label} (Year ${year})`,
      unit,
      deps: resolvedDeps,
      compute,
      formula: resolvedFormula,
      seriesMeta,
    };
  }

  return nodes;
}

/**
 * Create a cumulative sum over years
 * Example: cumulativeRentalIncome = sum of netRentalIncome from year 1 to current year
 */
export function cumulativeSum(
  baseId: string,
  config: {
    sourceBaseId: string;
    label: string;
    unit: Unit;
    startYear?: number;
    initialValue?: number;
    years?: number;
  }
): Record<NodeId, ComputedNode> {
  const { sourceBaseId, label, unit, startYear = 1, initialValue = 0, years = 11 } = config;

  const nodes: Record<NodeId, ComputedNode> = {};

  for (let year = 0; year < years; year++) {
    const id = temporalId(baseId, year);
    const seriesMeta = createSeriesMeta(baseId, 'cumulative-sum', year, years, label);

    if (year < startYear) {
      // Before start year, just return initial value
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit,
        deps: [],
        compute: () => initialValue,
        formula: String(initialValue),
        seriesMeta,
      };
    } else if (year === startYear) {
      // First year: initial + source
      const sourceId = temporalId(sourceBaseId, year);
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit,
        deps: [sourceId],
        compute: (source) => initialValue + source,
        formula: `${initialValue} + {${sourceId}}`,
        seriesMeta,
      };
    } else {
      // Subsequent years: previous cumulative + source
      const prevId = temporalId(baseId, year - 1);
      const sourceId = temporalId(sourceBaseId, year);
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit,
        deps: [prevId, sourceId],
        compute: (prev, source) => prev + source,
        formula: `{${prevId}} + {${sourceId}}`,
        seriesMeta,
      };
    }
  }

  return nodes;
}

/**
 * Portfolio growth with reinvestment
 * Example: investedRentalPortfolio that grows at blendedReturn and receives annual contributions
 */
export function portfolioGrowth(
  baseId: string,
  config: {
    contributionBaseId: string;
    returnRateId: NodeId;
    label: string;
    startYear?: number;
    years?: number;
  }
): Record<NodeId, ComputedNode> {
  const { contributionBaseId, returnRateId, label, startYear = 1, years = 11 } = config;

  const nodes: Record<NodeId, ComputedNode> = {};

  for (let year = 0; year < years; year++) {
    const id = temporalId(baseId, year);
    const seriesMeta = createSeriesMeta(baseId, 'portfolio-growth', year, years, label);

    if (year < startYear) {
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit: 'currency',
        deps: [],
        compute: () => 0,
        formula: '0',
        seriesMeta,
      };
    } else if (year === startYear) {
      const contributionId = temporalId(contributionBaseId, year);
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit: 'currency',
        deps: [contributionId],
        compute: (contribution) => contribution,
        formula: `{${contributionId}}`,
        seriesMeta,
      };
    } else {
      const prevId = temporalId(baseId, year - 1);
      const contributionId = temporalId(contributionBaseId, year);
      nodes[id] = {
        kind: 'computed',
        id,
        label: `${label} (Year ${year})`,
        unit: 'currency',
        deps: [prevId, returnRateId, contributionId],
        compute: (prev, returnRate, contribution) =>
          prev * (1 + returnRate / 100) + contribution,
        formula: `{${prevId}} × (1 + {${returnRateId}}/100) + {${contributionId}}`,
        seriesMeta,
      };
    }
  }

  return nodes;
}

/**
 * Helper to merge multiple node records
 */
export function mergeNodes(...records: Record<NodeId, Node>[]): Record<NodeId, Node> {
  return Object.assign({}, ...records);
}

/**
 * Series Configuration Types
 * Declarative API for creating temporal series
 */

export interface CompoundGrowthConfig {
  type: 'compound-growth';
  initial: NodeId;
  rate: NodeId;
  label: string;
  unit: Unit;
  years?: number;
}

export interface CumulativeSumConfig {
  type: 'cumulative-sum';
  source: string; // Base ID of source series
  label: string;
  unit: Unit;
  startYear?: number;
  initialValue?: number;
  years?: number;
}

export interface PortfolioGrowthConfig {
  type: 'portfolio-growth';
  contributions: string; // Base ID of contribution series
  returnRate: NodeId;
  label: string;
  startYear?: number;
  years?: number;
}

export interface TemporalComputedConfig {
  type: 'temporal-computed';
  deps: Array<{ id: NodeId; temporal: boolean }>;
  compute: (...values: number[]) => number;
  formula: string;
  label: string;
  unit: Unit;
  years?: number;
}

export type SeriesConfig =
  | CompoundGrowthConfig
  | CumulativeSumConfig
  | PortfolioGrowthConfig
  | TemporalComputedConfig;

/**
 * Declarative series creation
 *
 * @example
 * ```typescript
 * const propertyValue = series('propertyValue', {
 *   type: 'compound-growth',
 *   initial: 'purchasePrice',
 *   rate: 'appreciationRate',
 *   label: 'Property Value',
 *   unit: 'currency',
 * });
 * ```
 */
export function series(
  baseId: string,
  config: SeriesConfig
): Record<NodeId, ComputedNode> {
  switch (config.type) {
    case 'compound-growth':
      return compoundGrowth(baseId, {
        initialValueId: config.initial,
        growthRateId: config.rate,
        label: config.label,
        unit: config.unit,
        years: config.years,
      });

    case 'cumulative-sum':
      return cumulativeSum(baseId, {
        sourceBaseId: config.source,
        label: config.label,
        unit: config.unit,
        startYear: config.startYear,
        initialValue: config.initialValue,
        years: config.years,
      });

    case 'portfolio-growth':
      return portfolioGrowth(baseId, {
        contributionBaseId: config.contributions,
        returnRateId: config.returnRate,
        label: config.label,
        startYear: config.startYear,
        years: config.years,
      });

    case 'temporal-computed':
      return temporalComputed(baseId, {
        deps: config.deps,
        compute: config.compute,
        formula: config.formula,
        label: config.label,
        unit: config.unit,
        years: config.years,
      });
  }
}

/**
 * Helper to get all nodes belonging to a series
 */
export function getSeriesNodes(
  nodes: Record<NodeId, Node>,
  seriesId: string
): ComputedNode[] {
  return Object.values(nodes)
    .filter((node): node is ComputedNode =>
      node.kind === 'computed' &&
      node.seriesMeta?.seriesId === seriesId
    )
    .sort((a, b) => (a.seriesMeta?.year ?? 0) - (b.seriesMeta?.year ?? 0));
}

/**
 * Get pattern description for a series type
 */
export function getSeriesPatternDescription(pattern: SeriesPatternType): string {
  switch (pattern) {
    case 'compound-growth':
      return 'Value = Initial × (1 + Rate)^Year';
    case 'cumulative-sum':
      return 'Value = Sum of contributions from Year 1 to current year';
    case 'portfolio-growth':
      return 'Value = Previous × (1 + Return) + Contribution';
    case 'temporal-computed':
      return 'Value computed from other values at the same year';
    case 'custom':
      return 'Custom calculation';
  }
}
